/**
 * Local DockRail kit (Feeney task 2, spec p.8-9).
 *
 * Temporary stand-in until the real DockRail products are published on the
 * backend: the kit is assembled at runtime by cloning existing DesignRail
 * donor products (fascia 36" post, 200s top rail, 36" horizontal cable) with
 * railingType switched to DockRail. Models/dimensions/meshes come from the
 * donors, so nothing is authored client-side. Each local entry is skipped as
 * soon as the backend has a DockRail product of the same productType
 * (for corner posts — of the same angleType).
 *
 * Per spec "One type of DockRail Post for any position": both corner-post
 * variants reuse the Intermediate Post donor model.
 */

// Spec p.20: DockRail ships in exactly three colors — White, Black, Silver.
// Until a dedicated group exists server-side, a virtual group under this id
// is assembled from the existing /colors records (see
// mergeLocalDockRailColorGroup below) and the local products reference it.
export const LOCAL_DOCKRAIL_COLOR_GROUP_ID = 'local_dockrail_colors';

// Appends the virtual White/Black/Silver group to the loaded /ColorGroups
// list. Skipped once a group with this id is already present. Only color
// records referenced by an existing server group are considered — membership
// proves the record renders and applies correctly, while orphaned legacy
// duplicates (e.g. a 'Silver' carrying a wood texture on a dead host) are
// filtered out.
export function mergeLocalDockRailColorGroup(colorGroups: any[], colors: any[]): any[] {
  const groups = colorGroups || [];
  if (!colors || !colors.length) {
    return groups;
  }
  if (groups.some(g => g && g.id === LOCAL_DOCKRAIL_COLOR_GROUP_ID)) {
    return groups;
  }
  const referenced = new Set<string>();
  groups.forEach(g => ((g && g.colors) || []).forEach(id => referenced.add(id)));
  const pick = (name: string) =>
    colors.find(c => c && c.name === name && referenced.has(c.id)) ||
    colors.find(c => c && c.name === name);
  const trio = ['White', 'Black', 'Silver'].map(pick).filter(Boolean);
  if (trio.length < 3) {
    return groups;
  }
  return [
    ...groups,
    {
      id: LOCAL_DOCKRAIL_COLOR_GROUP_ID,
      name: 'DockRail Colors',
      colors: trio.map(c => c.id)
    }
  ];
}

export function mergeLocalDockRailKit(apiProducts: any[]): any[] {
  const products = apiProducts || [];

  const hasDock = (productType: string, extra: (p: any) => boolean = () => true) =>
    products.some(
      p =>
        p &&
        p.railingType &&
        p.railingType.includes('DockRail') &&
        p.productType === productType &&
        extra(p)
    );

  const findDesignRailDonor = (pred: (p: any) => boolean) =>
    products.find(
      p => p && p.railingType && p.railingType.includes('DesignRail') && pred(p)
    );

  const extras = [];
  const clone = (donor: any, overrides: any) =>
    extras.push({
      ...donor,
      railingType: ['DockRail'],
      colors: [LOCAL_DOCKRAIL_COLOR_GROUP_ID],
      ...overrides
    });

  const intPost = findDesignRailDonor(
    p =>
      p.productType === 'Intermediate Post' &&
      p.mountType === 'Fascia Mount' &&
      p.heightGroup === '36'
  );
  if (intPost) {
    if (!hasDock('Intermediate Post')) {
      clone(intPost, { id: 'local_dockrail_post' });
    }

    // Corner placement math (getPositionsForCornerPosts, fascia branch):
    //   outside 90° corner: n = +2.5" + offset*cos45  → donor offset gives
    //     n ≈ 0, the single post sits right on the corner — keep the donor.
    //   inside 90° corner:  n = -2.5" + offset*cos45  → donor offset gives
    //     n ≈ -12.75cm, pushing the post ~10cm past the rail ends into the
    //     notch (it belongs to the wide DOUBLE inside-corner assembly). For a
    //     single post flush with the rail-line intersection n must equal the
    //     fascia rail offset (2.5" + donor.offset ≈ -2.7cm), solved below.
    // outerOffset (cable end pull-back at corners) is half the post width
    // plus the rail offset — the donor has none, and undefined breaks the
    // cable ends at corners.
    const inch = 2.54;
    const railOffset = 2.5 * inch + (+intPost.offset || 0);
    const outerOffset = (intPost.width || 6) / 2 + Math.abs(railOffset);

    if (!hasDock('Corner Post', p => p.angleType === 'Outside')) {
      clone(intPost, {
        id: 'local_dockrail_corner_outside',
        productType: 'Corner Post',
        cornerType: 'Single',
        angleType: 'Outside',
        outerOffset
      });
    }
    // fascia mounting also requests the inside-corner variant
    if (!hasDock('Corner Post', p => p.angleType === 'Inside')) {
      clone(intPost, {
        id: 'local_dockrail_corner_inside',
        productType: 'Corner Post',
        cornerType: 'Single',
        angleType: 'Inside',
        offset: String((railOffset + 2.5 * inch) / Math.cos(Math.PI / 4)),
        outerOffset
      });
    }
  }

  const topRail = findDesignRailDonor(
    p => p.productType === 'Handrail' && p.handrailType === '200s' && p.heightGroup === '36'
  );
  if (topRail && !hasDock('Handrail')) {
    clone(topRail, { id: 'local_dockrail_toprail' });
  }

  const cable = findDesignRailDonor(
    p => p.productType === 'Horizontal Cable' && p.heightGroup === '36'
  );
  if (cable && !hasDock('Horizontal Cable')) {
    clone(cable, { id: 'local_dockrail_cable' });
  }

  return extras.length ? [...products, ...extras] : products;
}



// WEBPACK FOOTER //
// ./src/app/models/2d3d/dockrail-local.config.ts